import React, { useReducer, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { userAppData } from '../MainApp.js';
import { StandardSingleLineTextEntry } from '../common/formComponents/StandardSingleLineTextEntry.js';
import { StandardMultiLineTextEntry } from '../common/formComponents/StandardMultiLineTextEntry.js';
import { StandardDateSelector } from '../common/formComponents/StandardDateSelector.js';
import LinksComponent from '../common/formComponents/LinksComponent';
import useS3FileUpload from '../hooks/useS3FileUpload';
import {
  FileDropzone,
  SubmitButton,
} from '../common/formComponents.js';
import { SEC_DataApiPost } from '../func.js';
import Header from '../common/header.js';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import '../common/requestForm.css';


// Initial States
const initialFormState = {
  title: '',
  location: '',
  description: '',
  requiredDate: '',
  files: [],
  links: [],
  rawLinks: [], // <-- add this
  errors: {},
  isSubmitting: false,
  resetKey: 0
};

// Reducer Function
function formReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_ERROR':
      return { ...state, errors: { ...state.errors, [action.field]: action.error } };
    case 'CLEAR_ERRORS':
      return { ...state, errors: {} };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.value };
    case 'RESET_FORM':
      return { ...initialFormState, resetKey: state.resetKey + 1 };
    default:
      return state;
  }
}

export default function ItSupportForm() {
  const [state, dispatch] = useReducer(formReducer, initialFormState);
  const [submitStatus, setSubmitStatus] = useState('');
  const { uploadFiles } = useS3FileUpload('itRequests');


  // Utility Functions
  const validateField = (field, value) => {
    if (!value || (field === 'requiredDate' && value === '0000-00-00')) {
      return true; // just indicate invalid
    }

    if (field === 'links') {
      const hasUrlWithNoName = value.some(link =>
        link.url && link.url.trim() !== '' && (!link.name || link.name.trim() === '')
      );
      if (hasUrlWithNoName) return true;
    }

    return null; // valid
  };


  const requiredFields = ['title', 'location', 'description', 'requiredDate'];
  const validateForm = () => {
    const errors = requiredFields.reduce((acc, field) => {
      const error = validateField(field, state[field]);
      if (error) acc[field] = error;
      return acc;
    }, {});
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    dispatch({ type: 'CLEAR_ERRORS' });
    dispatch({ type: 'SET_SUBMITTING', value: true });

    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      const fieldLabels = {
        title: "Request Title",
        location: "Location",
        description: "Description",
        requiredDate: "Required Date",
        links: "Links"
      };

      const missingFields = Object.keys(errors)
        .map(field => fieldLabels[field] || field)
        .join('\n');

      toast.error(`The following fields are blank:\n${missingFields}`, {
        autoClose: 6000,
        position: "top-right",
        style: { whiteSpace: 'pre-line' }
      });


      dispatch({ type: 'SET_ERROR', field: 'form', error: errors });
      dispatch({ type: 'SET_SUBMITTING', value: false });
      setSubmitStatus('');
      return;
    }

    // Check for links with a URL but no name
    const hasIncompleteLinks = state.rawLinks.some(link =>
      (link.name && link.name.trim() !== '' && (!link.url || link.url.trim() === '')) ||
      (link.url && link.url.trim() !== '' && (!link.name || link.name.trim() === ''))
    );

    if (hasIncompleteLinks) {
      toast.error("⚠️ Each link must include both a name and a URL", {
        autoClose: 5000,
        position: "top-right",
        style: { whiteSpace: 'pre-line' }
      });
      dispatch({ type: 'SET_SUBMITTING', value: false });
      setSubmitStatus('');
      return;
    }

    // Show spinner when validation passes



    const timestamp = new Date().toISOString();
    const eventObject = {
      primaryEvent: {
        user: userAppData.cogId,
        timestamp,
        eventData: state.description,
      },
      additionalEvents: [],
    };

    const formObj = {
      title: state.title,
      location: state.location,
      events: eventObject,
      requiredDate: state.requiredDate,
      fileNames: [],
      uploadGroupId: null,
      links: state.links,
      subDate: timestamp.split('T')[0],
    };

    try {
      // Use your new uploadFiles function from the hook
      if (state.files.length > 0) {
        setSubmitStatus('Uploading files...');
        const uploadGroupId = uuidv4();
        const uploaded = await uploadFiles(state.files, userAppData.cogId, { uploadGroupId });
        formObj.fileNames = uploaded;
        formObj.uploadGroupId = uploadGroupId;
      }

      setSubmitStatus('Submitting request...');
      const response = await SEC_DataApiPost('it-requests', JSON.stringify(formObj), {}, () => {
        throw new Error('Database submission failed');
      });

      const rid = response?.requestId;
      if (!rid || rid === 'error') {
        throw new Error('Invalid or missing ID in response');
      }

      toast.success(`IT request submitted with ID ${rid}`, {
        autoClose: 6000,
        position: "top-right",
      });
      dispatch({ type: 'RESET_FORM' });

    } catch (err) {
      console.error('Submission error:', err);
      toast.error('❌ Failed to submit request. Please try again.', {
        autoClose: 5000,
        position: "top-right",
      });
    } finally {
      dispatch({ type: 'SET_SUBMITTING', value: false });
      setSubmitStatus('');
    }
  };






  return (
    <div className='internal_main_div'>
      <Header title='IT Support Form' subtitle='Submit a new IT support request.' />
      <div className='formDiv request-form-shell'>
        <form onSubmit={handleSubmit}>
          <div className='request-form-section'>
            <h2 className='request-form-section-title'>Request Details</h2>
            <StandardSingleLineTextEntry
              title='Request Title'
              data={state.title}
              setData={(value) => dispatch({ type: 'SET_FIELD', field: 'title', value })}
              errorText={state.errors.title}
            />
            <StandardSingleLineTextEntry
              title='Location'
              data={state.location}
              setData={(value) => dispatch({ type: 'SET_FIELD', field: 'location', value })}
              errorText={state.errors.location}
            />
            <StandardMultiLineTextEntry
              title='Description'
              data={state.description}
              setData={(value) => dispatch({ type: 'SET_FIELD', field: 'description', value })}
              errorText={state.errors.description}
            />
            <StandardDateSelector
              title='Required Date'
              value={state.requiredDate}
              setData={(value) => dispatch({ type: 'SET_FIELD', field: 'requiredDate', value })}
              allowPast={false}
              errorText={state.errors.requiredDate}
            />
          </div>

          <div className='request-form-section'>
            <h2 className='request-form-section-title'>Files and Links</h2>
            <LinksComponent
              resetTrigger={state.resetKey}
              onChange={(cleanedLinks, rawLinks) => {
                dispatch({ type: 'SET_FIELD', field: 'links', value: cleanedLinks });
                dispatch({ type: 'SET_FIELD', field: 'rawLinks', value: rawLinks });

                const hasInvalid = rawLinks?.some(link =>
                  (link.name && link.name.trim() !== '' && (!link.url || link.url.trim() === '')) ||
                  (link.url && link.url.trim() !== '' && (!link.name || link.name.trim() === ''))
                );

                if (hasInvalid) {
                  dispatch({ type: 'SET_ERROR', field: 'links', error: 'Each link must include both a name and a URL.' });
                } else {
                  dispatch({ type: 'SET_ERROR', field: 'links', error: null });
                }
              }}
            />

            <FileDropzone
              filesState={state.files}
              fileStateSet={(files) => dispatch({ type: 'SET_FIELD', field: 'files', value: files })}
              maxFiles={10}
              maxTotalSizeMB={50}
              enableClipboard
              labelText='Files'
              noteText='Files will not be uploaded until the request is submitted.'
            />
          </div>

          <SubmitButton text='Submit Request' isLoading={state.isSubmitting} loadingText={submitStatus} />
        </form>
      </div>
    </div>
  );
}
